import React, { useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Button } from '@progress/kendo-react-buttons';
import { toastStore } from '../../stores/toast-store';
import { SortMode } from '../../types/tilo/tilo-game';
import { useTiloFacilitator } from '../../hooks/use-tilo-facilitator';
import { StatusBadge } from '../../components/tilo/status-badge';
import { TeamCard } from '../../components/tilo/team-card';
import { Logo } from '../../components/logo';
import { UserCard } from '../../components/header/user-card';
import { UserIcon } from '../../components/header/user-icon';

const formatDate = (d: Date | string) =>
  d instanceof Date ? d.toLocaleString() : String(d);

const gameTypeLabel: Record<string, string> = {
  test: 'Test',
  demo: 'Demo',
  live: 'Live'
};

const sortButtons: { label: string; value: SortMode }[] = [
  { label: 'Name', value: 'name' },
  { label: 'Score', value: 'score' },
  { label: 'Rank', value: 'rank' }
];

interface InfoStatProps {
  label: string;
  value: React.ReactNode;
}

const InfoStat = ({ label, value }: InfoStatProps) => (
  <div className={'tilo-info-stat'}>
    <span className={'tilo-info-stat__label'}>{label}</span>
    <span className={'tilo-info-stat__value'}>{value}</span>
  </div>
);

export const TiloFacilitatorPage = () => {
  const { gameId } = useParams<{ gameId: string }>();
  const [searchParams] = useSearchParams();
  const playlistId = Number(searchParams.get('playlistId')) || undefined;

  const {
    game,
    isLoading,
    timeleft,
    sortedTeams,
    sortBy,
    setSortBy,
    scoresHidden,
    startGame,
    finishGame,
    addTeams,
    addTime,
    toggleScores,
    addTeamScore,
    giveToken,
    kickPlayer
  } = useTiloFacilitator(gameId!, playlistId);

  const [teamsToAdd, setTeamsToAdd] = useState(1);
  const [timeDelta, setTimeDelta] = useState(0);
  const [finishState, setFinishState] = useState<'idle' | 'pending' | 'done'>('idle');

  if (isLoading || !game) {
    return (
      <div className={'d-flex flex-column align-items-center justify-content-center h-100 gap-2'}>
        <span className={'loader'} />
        <label className={'text-muted'}>Loading game…</label>
      </div>
    );
  }

  // ── Handlers ──────────────────────────────────────────────────────────────

  const handleStart = async () => {
    try {
      await startGame();
      toastStore.show('Game Started', `${game.name} is now running`, 'success', 4000);
    } catch {
      toastStore.show('Error', 'Failed to start game', 'error', 4000);
    }
  };

  const handleFinish = async () => {
    if (!window.confirm('Finish this game? This cannot be undone.')) return;
    setFinishState('pending');
    try {
      await finishGame();
      setFinishState('done');
      toastStore.show('Game Finished', `${game.name} has ended`, 'success', 4000);
    } catch {
      setFinishState('idle');
      toastStore.show('Error', 'Failed to finish game', 'error', 4000);
    }
  };

  const handleAddTeams = async () => {
    if (teamsToAdd < 1) return;
    try {
      await addTeams(teamsToAdd);
      toastStore.show('Teams Added', `${teamsToAdd} team(s) added`, 'success', 3000);
    } catch {
      toastStore.show('Error', 'Failed to add teams', 'error', 3000);
    }
  };

  const handleAddTime = async () => {
    const newTotal = game.playtime + timeDelta;
    if (newTotal < 0 || newTotal > 210) {
      toastStore.show(
        'Invalid',
        `Select a time between -${game.playtime} and ${210 - game.playtime}`,
        'error',
        4000
      );
      return;
    }
    try {
      await addTime(timeDelta);
      toastStore.show(
        'Time Adjusted',
        `${timeDelta > 0 ? '+' : ''}${timeDelta} min`,
        'success',
        3000
      );
      setTimeDelta(0);
    } catch {
      toastStore.show('Error', 'Failed to adjust time', 'error', 3000);
    }
  };


  const handleToggleScores = async () => {
    try {
      await toggleScores(!scoresHidden);
      toastStore.show(
        'Scores',
        scoresHidden ? 'Scores are now visible' : 'Scores are now hidden',
        'success',
        3000
      );
    } catch {
      toastStore.show('Error', 'Failed to toggle scores', 'error', 3000);
    }
  };

  const handleCopyUrl = () => {
    if (!game.URL) return;
    navigator.clipboard.writeText(game.URL);
    toastStore.show('Copied', 'Participant link copied to clipboard', 'success', 2000);
  };

  const timerText = game.finished
    ? '00:00:00'
    : game.started
    ? `${timeleft.hour}:${timeleft.min}:${timeleft.sec}`
    : `${game.playtime} min`;

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className={'tilo-page'}>

      {/* ── Page Header (matches page-base__header style) ── */}
      <div className={'tilo-page-header'}>
        <Logo primaryColor={'#4698cb'} width={'2.5rem'} height={'2.5rem'} />

        <div className={'tilo-page-header__title-group'}>
          <span className={'tilo-page-header__subtitle'}>
            {game.client} · {gameTypeLabel[game.gameType] ?? game.gameType}
          </span>
          <span className={'tilo-page-header__name'}>{game.name}</span>
        </div>

        <StatusBadge started={game.started} finished={game.finished} />

        <div className={'tilo-page-header__user'}>
          <UserCard />
          <UserIcon />
        </div>
      </div>

      {/* ── Main ── */}
      <div className={'tilo-main'}>

        {/* ── Game Control strip (matches GameControl style) ── */}
        <div className={'tilo-game-control'}>
          <span className={'tilo-game-control__timer'}>
            Time Left : <span>{timerText}</span>
          </span>
          <div className={'tilo-game-control__actions'}>
            {!game.started && !game.finished && (
              <Button themeColor={'success'} onClick={handleStart}>
                <span className={'tilo-btn-content'}>
                  <span className={'material-symbols-outlined'}>play_arrow</span>
                  Start Game
                </span>
              </Button>
            )}
            {(game.started && !game.finished) || finishState !== 'idle' ? (
              <div className={'tilo-finish-group'}>
                <Button
                  themeColor={'error'}
                  fillMode={'outline'}
                  disabled={finishState !== 'idle'}
                  onClick={handleFinish}>
                  <span className={'tilo-btn-content'}>
                    <span className={'material-symbols-outlined'}>stop</span>
                    {finishState === 'pending' ? 'Finishing…' : 'Finish Game'}
                  </span>
                </Button>
                {finishState === 'done' && (
                  <span className={'tilo-finish-group__label'}>Game Finished</span>
                )}
              </div>
            ) : null}
          </div>
        </div>

        {/* ── Participant Link ── */}
        <div className={'tilo-section'}>
          <div className={'tilo-participant-link'}>
            <span className={'tilo-participant-link__label'}>Participant Link</span>
            {game.URL ? (
              <>
                <a
                  href={game.URL}
                  target={'_blank'}
                  rel={'noreferrer'}
                  className={'tilo-participant-link__url'}>
                  {game.URL}
                </a>
                <button
                  className={'tilo-participant-link__copy'}
                  title={'Copy link'}
                  onClick={handleCopyUrl}>
                  <span className={'material-symbols-outlined'}>content_copy</span>
                  Copy
                </button>
                <a
                  href={game.URL}
                  target={'_blank'}
                  rel={'noreferrer'}
                  className={'tilo-participant-link__open'}
                  title={'Open in new tab'}>
                  <span className={'material-symbols-outlined'}>open_in_new</span>
                  Open
                </a>
              </>
            ) : (
              <span className={'tilo-participant-link__empty'}>Not configured</span>
            )}
          </div>
        </div>

        {/* ── Game Info ── */}
        <div className={'tilo-section'}>
          <div className={'tilo-info-stats'}>
            <InfoStat label={'Client'} value={game.client} />
            <InfoStat label={'Session'} value={gameTypeLabel[game.gameType] ?? game.gameType} />
            <InfoStat label={'Doors'} value={game.nbrDoors} />
            <InfoStat label={'Teams'} value={game.teamsCount} />
            <InfoStat label={'Players / Team'} value={game.nbrPlayersPerTeam} />
            <InfoStat label={'Playtime'} value={`${game.playtime} min`} />
            <InfoStat label={'Language'} value={game.language} />
            <InfoStat label={'Started At'} value={game.started ? formatDate(game.startedAt) : 'Not started yet'} />
            <InfoStat label={'Finished At'} value={game.finished ? formatDate(game.finishedAt) : 'Not finished yet'} />
          </div>
        </div>

        {/* ── Controls (only when game not finished) ── */}
        {!game.finished && (
          <div className={'tilo-section'}>
            <div className={'tilo-controls'}>
              <div className={'tilo-control-group'}>
                <span className={'tilo-control-group__label'}>Adjust Time</span>
                <input
                  type={'number'}
                  value={timeDelta}
                  onChange={(e) => setTimeDelta(parseInt(e.target.value, 10) || 0)}
                  className={'tilo-input tilo-input--small'}
                  placeholder={'±min'}
                />
                <Button size={'small'} themeColor={'primary'} onClick={handleAddTime}>
                  Apply
                </Button>
              </div>

              <div className={'tilo-control-group'}>
                <span className={'tilo-control-group__label'}>Add Teams</span>
                <input
                  type={'number'}
                  min={1}
                  value={teamsToAdd}
                  onChange={(e) =>
                    setTeamsToAdd(Math.max(1, parseInt(e.target.value, 10) || 1))
                  }
                  className={'tilo-input tilo-input--small'}
                />
                <Button size={'small'} themeColor={'primary'} onClick={handleAddTeams}>
                  Add
                </Button>
              </div>


              <div className={'tilo-control-group'}>
                <label className={'tilo-toggle'}>
                  <input
                    type={'checkbox'}
                    checked={scoresHidden}
                    onChange={handleToggleScores}
                  />
                  <span className={'tilo-toggle__track'}>
                    <span className={'tilo-toggle__thumb'} />
                  </span>
                  <span className={'tilo-toggle__label'}>Hide Scores</span>
                </label>
              </div>
            </div>
          </div>
        )}

        {/* ── Teams ── */}
        <div className={'tilo-teams-section'}>
          <div className={'tilo-teams-toolbar'}>
            <span className={'text-muted'} style={{ fontSize: 13 }}>Sort teams</span>
            {sortButtons.map(({ label, value }) => (
              <Button
                key={value}
                size={'small'}
                fillMode={sortBy === value ? 'solid' : 'flat'}
                themeColor={sortBy === value ? 'primary' : 'base'}
                onClick={() => setSortBy(value)}>
                {label}
              </Button>
            ))}
            <span className={'ms-auto text-muted'} style={{ fontSize: 13 }}>
              {sortedTeams.length} team{sortedTeams.length !== 1 ? 's' : ''}
            </span>
          </div>

          {sortedTeams.map((team) => (
            <TeamCard
              key={team._id}
              team={team}
              nbrPlayersPerTeam={game.nbrPlayersPerTeam}
              onAddScore={addTeamScore}
              onGiveToken={giveToken}
              onKick={kickPlayer}
            />
          ))}

          {sortedTeams.length === 0 && (
            <div className={'tilo-empty-state'}>
              <span className={'material-symbols-outlined'} style={{ fontSize: 48 }}>group</span>
              <span>No teams yet</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};